import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  Modal,
  Button,
  Fade,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
  Switch,
} from '@mui/material';
import {
  Visibility,
  Edit,
  Save,
  Close,
  Add,
} from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  getHotelSupplierConfigurationsAPI,
  viewHotelSupplierConfigurationAPI,
  updateHotelSupplierConfigurationAPI,
  saveHotelSupplierConfigurationAPI,
} from '../operations/supplierService';

/* ================= TYPES ================= */
interface HotelSupplierData {
  hotel_supplier_id: number;
  hotel_supplier_name: string;
  isActive: boolean;
  timeout: number;
  environment: string;
  integrated_with: string;
  supplier_prefix: string;
  provider_mapping: string;
  booking_enable: boolean;
}

const HotelSupplierConfiguration = () => {
  const [openRowId, setOpenRowId] = useState<number | null>(null);
  const [editRowId, setEditRowId] = useState<number | null>(null);
  const [openedByEdit, setOpenedByEdit] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [suppliers, setSuppliers] = useState<HotelSupplierData[]>([]);
  const [editedData, setEditedData] = useState<Partial<HotelSupplierData>>({});
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [viewLoading, setViewLoading] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [viewDataMap, setViewDataMap] = useState<Record<number, Partial<HotelSupplierData>>>({});
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMsg, setSnackbarMsg] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [newSupplierErrors, setNewSupplierErrors] = useState<Record<string, string>>({});
  const [newSupplier, setNewSupplier] = useState<Partial<HotelSupplierData>>({
    hotel_supplier_name: '',
    isActive: true,
    timeout: 10000,
    environment: 'prod',
    integrated_with: 'API',
    supplier_prefix: '',
    provider_mapping: '',
    booking_enable: false,
  });

  const toBool = (v: any): boolean =>
    v === true || v === 1 || v === '1' || v === 'true';

  const toFlag = (v: any): 0 | 1 => (toBool(v) ? 1 : 0);

  /* ================= SNACKBAR HELPERS ================= */
  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'info' | 'warning' = 'success'
  ) => {
    setSnackbarMsg(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (
    _event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  /* ================= FETCH API ================= */
  const fetchSupplierConfigs = async () => {
    try {
      setLoading(true);
      const response = await getHotelSupplierConfigurationsAPI();
      const rawData = response?.data ?? response ?? [];

      const normalizedData: HotelSupplierData[] = (Array.isArray(rawData) ? rawData : []).map(
        (item: any) => ({
          hotel_supplier_id: item.hotel_supplier_id,
          hotel_supplier_name: item.hotel_supplier_name ?? '',
          isActive: toBool(item.isActive),
          timeout: Number(item.timeout) || 10000,
          environment: item.environment ?? 'prod',
          integrated_with: item.integrated_with ?? 'API',
          supplier_prefix: item.supplier_prefix ?? '',
          provider_mapping: item.provider_mapping ?? '',
          booking_enable: toBool(item.booking_enable),
        })
      );

      setSuppliers(normalizedData);
    } catch (err) {
      console.error('Failed to fetch hotel supplier configurations:', err);
      showSnackbar('Failed to fetch hotel supplier configurations', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSupplierConfigs();
  }, []);

  /* ================= VIEW API ================= */
  const toggleViewRow = async (id: number) => {
    if (openRowId === id) {
      setOpenRowId(null);
      setOpenedByEdit(false);
      return;
    }

    setOpenRowId(id);
    setEditRowId(null);
    setOpenedByEdit(false);

    if (viewDataMap[id]) return;

    const supplier = suppliers.find((s) => s.hotel_supplier_id === id);
    if (!supplier) return;

    try {
      setViewLoading(true);
      const payload = {
        hotel_supplier_name: supplier.hotel_supplier_name,
        environment: supplier.environment,
      };

      const response = await viewHotelSupplierConfigurationAPI(payload);
      const viewResult = response?.data ?? response ?? {};

      const normalizedViewData: Partial<HotelSupplierData> = {
        timeout: Number(viewResult.timeout) || supplier.timeout,
        isActive: supplier.isActive,
        supplier_prefix: viewResult.supplier_prefix ?? supplier.supplier_prefix,
        provider_mapping: viewResult.provider_mapping ?? supplier.provider_mapping,
        booking_enable: supplier.booking_enable,
        integrated_with: viewResult.integrated_with ?? supplier.integrated_with,
      };

      setViewDataMap((prev) => ({ ...prev, [id]: normalizedViewData }));
      setSuppliers((prev) =>
        prev.map((s) => (s.hotel_supplier_id === id ? { ...s, ...normalizedViewData } : s))
      );
    } catch (err) {
      console.error('Failed to fetch hotel supplier view details:', err);
      showSnackbar('Failed to load hotel supplier details', 'error');
    } finally {
      setViewLoading(false);
    }
  };

  const toggleEditRow = (id: number, supplier: HotelSupplierData) => {
    if (editRowId === id) {
      setEditRowId(null);
      setEditedData({});
    } else {
      if (openRowId !== id) setOpenedByEdit(true);
      setEditRowId(id);
      setOpenRowId(id);
      setEditedData({
        ...supplier,
        isActive: toBool(supplier.isActive),
        booking_enable: toBool(supplier.booking_enable),
      });
    }
  };

  /* ================= UPDATE API ================= */
  const handleSave = async (id: number) => {
    const originalSupplier = suppliers.find((s) => s.hotel_supplier_id === id);
    if (!originalSupplier) return;

    const hasChanges = Object.keys(editedData).some((key) => {
      const field = key as keyof HotelSupplierData;
      return editedData[field] !== originalSupplier[field];
    });

    if (!hasChanges) {
      showSnackbar("Didn't update anything", 'info');
      setEditRowId(null);
      setEditedData({});
      return;
    }

    try {
      setSaveLoading(true);
      const merged = { ...originalSupplier, ...editedData };

      const updatePayload = {
        hotel_supplier_id: merged.hotel_supplier_id,
        hotel_supplier_name: merged.hotel_supplier_name || '',
        isActive: toFlag(merged.isActive),
        timeout: merged.timeout || 10000,
        environment: merged.environment || 'prod',
        integrated_with: merged.integrated_with || 'API',
        supplier_prefix: merged.supplier_prefix || '',
        provider_mapping: merged.provider_mapping || '',
        booking_enable: toFlag(merged.booking_enable),
      };

      await updateHotelSupplierConfigurationAPI(updatePayload);
      showSnackbar('Hotel supplier configuration updated successfully', 'success');

      setViewDataMap((prev) => {
        const newMap = { ...prev };
        delete newMap[id];
        return newMap;
      });

      await fetchSupplierConfigs();
      setEditRowId(null);
      setEditedData({});
      if (openedByEdit) {
        setOpenRowId(null);
        setOpenedByEdit(false);
      }
    } catch (err) {
      console.error('Failed to update hotel supplier configuration:', err);
      showSnackbar('Failed to update hotel supplier configuration', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCancel = () => {
    setEditRowId(null);
    setEditedData({});
    if (openedByEdit) {
      setOpenRowId(null);
      setOpenedByEdit(false);
    }
  };

  const handleEditChange = (field: keyof HotelSupplierData, value: any) => {
    setEditedData((prev) => ({ ...prev, [field]: value }));
  };

  const handleModalOpen = () => setOpenModal(true);
  const handleModalClose = () => {
    setOpenModal(false);
    setNewSupplierErrors({});
    setNewSupplier({
      hotel_supplier_name: '',
      isActive: true,
      timeout: 10000,
      environment: 'prod',
      integrated_with: 'API',
      supplier_prefix: '',
      provider_mapping: '',
      booking_enable: false,
    });
  };

  const handleNewSupplierChange = (field: keyof HotelSupplierData, value: any) => {
    setNewSupplier((prev) => ({ ...prev, [field]: value }));
    setNewSupplierErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  /* ================= VALIDATION ================= */
  const validateNewSupplier = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!newSupplier.hotel_supplier_name?.trim()) errors.hotel_supplier_name = 'Please fill the fields';
    if (!newSupplier.environment) errors.environment = 'Please fill the fields';
    if (!newSupplier.timeout) errors.timeout = 'Please fill the fields';
    if (!newSupplier.supplier_prefix?.trim()) errors.supplier_prefix = 'Please fill the fields';
    if (!newSupplier.provider_mapping?.trim()) errors.provider_mapping = 'Please fill the fields';
    return errors;
  };

  /* ================= SAVE NEW API ================= */
  const handleSaveNewSupplier = async () => {
    const errors = validateNewSupplier();
    if (Object.keys(errors).length > 0) {
      setNewSupplierErrors(errors);
      showSnackbar('Please fill all required fields', 'warning');
      return;
    }
    setNewSupplierErrors({});
    try {
      setSaveLoading(true);

      const payload = {
        hotel_supplier_name: newSupplier.hotel_supplier_name || '',
        isActive: toFlag(newSupplier.isActive),
        timeout: newSupplier.timeout || 10000,
        environment: newSupplier.environment || 'prod',
        integrated_with: newSupplier.integrated_with || 'API',
        supplier_prefix: newSupplier.supplier_prefix || '',
        provider_mapping: newSupplier.provider_mapping || '',
        booking_enable: toFlag(newSupplier.booking_enable),
      };

      await saveHotelSupplierConfigurationAPI(payload);
      showSnackbar('New hotel supplier configuration saved successfully', 'success');
      handleModalClose();
      await fetchSupplierConfigs();
    } catch (err) {
      console.error('Failed to save new hotel supplier configuration:', err);
      showSnackbar('Failed to save new hotel supplier configuration', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  const capitalize = (value?: string | null) => {
    if (!value) return '-';
    return value
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getEnvironmentColor = (env: string) => {
    switch (env.toLowerCase()) {
      case 'prod':
      case 'production':
        return { textColor: '#059669', bgColor: '#d1fae5', borderColor: '#a7f3d0' };
      case 'uat':
      case 'staging':
        return { textColor: '#d97706', bgColor: '#fef3c7', borderColor: '#fde68a' };
      case 'dev':
      case 'development':
        return { textColor: '#2563eb', bgColor: '#dbeafe', borderColor: '#93c5fd' };
      default:
        return { textColor: '#6b7280', bgColor: '#f3f4f6', borderColor: '#d1d5db' };
    }
  };

  const filteredSuppliers = suppliers.filter((supplier) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      supplier.hotel_supplier_name.toLowerCase().includes(searchLower) ||
      supplier.integrated_with.toLowerCase().includes(searchLower)
    );
  });

  const isEditing = (id: number) => editRowId === id;

  return (
    <>
      <Box className="headingBtnWrap" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Hotel Supplier Configuration
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleModalOpen}
          sx={{
            backgroundColor: '#272727',
            '&:hover': { backgroundColor: '#1a1a1a' },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Add New
        </Button>
      </Box>

      {/* Add New Hotel Supplier Modal */}
      <Modal
        open={openModal}
        onClose={handleModalClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
        }}
      >
        <Fade in={openModal}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: '#f5f5f5',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            className="supplierConfigurationPopup"
          >
            <CancelIcon onClick={handleModalClose} className="closeModal" />
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Add New Hotel Supplier
            </Typography>

            <Box className="feeModalForm">
              <Box className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {/* Hotel Supplier Name */}
                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                  <label>Hotel Supplier Name</label>
                  <input
                    type="text"
                    className="inputField"
                    style={{ minWidth: '260px', height: '38px', ...(newSupplierErrors.hotel_supplier_name ? { border: '1px solid red' } : {}) }}
                    value={newSupplier.hotel_supplier_name || ''}
                    onChange={(e) => handleNewSupplierChange('hotel_supplier_name', e.target.value)}
                  />
                </Box>

                {/* Environment */}
                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                  <label>Environment</label>
                  <FormControl sx={{ minWidth: 260, ...(newSupplierErrors.environment ? { '& .commondropdown': { border: '1px solid red !important' } } : {}) }} className="commondropdownWrap">
                    <Select
                      className="commondropdown superdropdown"
                      value={newSupplier.environment || ''}
                      onChange={(e) => handleNewSupplierChange('environment', e.target.value)}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return <span style={{ color: '#888' }}>Select</span>;
                        }
                        return capitalize(selected);
                      }}
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="prod">Production</MenuItem>
                      <MenuItem value="uat">UAT</MenuItem>
                      <MenuItem value="dev">Development</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Timeout */}
                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                  <label>Timeout (ms)</label>
                  <input
                    type="number"
                    className="inputField"
                    style={{ minWidth: '260px', height: '38px', ...(newSupplierErrors.timeout ? { border: '1px solid red' } : {}) }}
                    value={newSupplier.timeout || ''}
                    onChange={(e) => handleNewSupplierChange('timeout', e.target.value)}
                  />
                </Box>

                {/* Integrated With */}
                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                  <label>Integrated With</label>
                  <input
                    type="text"
                    className="inputField"
                    style={{ minWidth: '260px', height: '38px' }}
                    value={newSupplier.integrated_with || ''}
                    onChange={(e) => handleNewSupplierChange('integrated_with', e.target.value)}
                  />
                </Box>

                {/* Supplier Prefix */}
                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                  <label>Supplier Prefix</label>
                  <input
                    type="text"
                    className="inputField"
                    style={{ minWidth: '260px', height: '38px', ...(newSupplierErrors.supplier_prefix ? { border: '1px solid red' } : {}) }}
                    value={newSupplier.supplier_prefix || ''}
                    onChange={(e) => handleNewSupplierChange('supplier_prefix', e.target.value)}
                  />
                </Box>

                {/* Provider Mapping */}
                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                  <label>Provider Mapping</label>
                  <input
                    type="text"
                    className="inputField"
                    style={{ minWidth: '260px', height: '38px', ...(newSupplierErrors.provider_mapping ? { border: '1px solid red' } : {}) }}
                    value={newSupplier.provider_mapping || ''}
                    onChange={(e) => handleNewSupplierChange('provider_mapping', e.target.value)}
                  />
                </Box>

                {/* Booking Enable */}
                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                  <label>Booking Enable</label>
                  <Box
                    onClick={() => handleNewSupplierChange('booking_enable', !newSupplier.booking_enable)}
                    sx={{
                      minWidth: 260,
                      height: 38,
                      px: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      border: '1px solid',
                      borderColor: newSupplier.booking_enable ? '#a7f3d0' : '#e5e7eb',
                      borderRadius: '4px',
                      bgcolor: newSupplier.booking_enable ? '#f0fdf4' : '#f9fafb',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s, border-color 0.15s',
                      userSelect: 'none',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: newSupplier.booking_enable ? '#059669' : '#9ca3af',
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: newSupplier.booking_enable ? '#059669' : '#6b7280',
                        }}
                      >
                        {newSupplier.booking_enable ? 'Enabled' : 'Disabled'}
                      </Typography>
                    </Box>
                    <Switch
                      size="small"
                      checked={!!newSupplier.booking_enable}
                      color="success"
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => handleNewSupplierChange('booking_enable', e.target.checked)}
                    />
                  </Box>
                </Box>

                {/* Status */}
                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                  <label>Status</label>
                  <Box
                    onClick={() => handleNewSupplierChange('isActive', !newSupplier.isActive)}
                    sx={{
                      minWidth: 260,
                      height: 38,
                      px: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      border: '1px solid',
                      borderColor: newSupplier.isActive ? '#a7f3d0' : '#e5e7eb',
                      borderRadius: '4px',
                      bgcolor: newSupplier.isActive ? '#f0fdf4' : '#f9fafb',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s, border-color 0.15s',
                      userSelect: 'none',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: newSupplier.isActive ? '#059669' : '#9ca3af',
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: newSupplier.isActive ? '#059669' : '#6b7280',
                        }}
                      >
                        {newSupplier.isActive ? 'Active' : 'Inactive'}
                      </Typography>
                    </Box>
                    <Switch
                      size="small"
                      checked={!!newSupplier.isActive}
                      color="success"
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => handleNewSupplierChange('isActive', e.target.checked)}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Save Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveNewSupplier}
                  disabled={saveLoading}
                  sx={{
                    backgroundColor: '#272727',
                    '&:hover': { backgroundColor: '#272727' },
                    textTransform: 'none',
                  }}
                >
                  {saveLoading ? <CircularProgress size={24} color="inherit" /> : 'SAVE'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Table */}
      <Box
        sx={{
          border: '1px solid #e5e7eb',
          borderRadius: 2,
          overflow: 'hidden',
        }}
        className="supplierConfiguration"
      >
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: '75vh',
            overflowY: 'auto',
            backgroundColor: '#ffffff',
          }}
        >
          <Table stickyHeader>
            <TableHead
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: '#f1f3f4',
              }}
            >
              <TableRow
                sx={{
                  boxShadow: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
                }}
              >
                {['SUPPLIER NAME', 'ENVIRONMENT', 'INTEGRATED WITH', 'ACTIVE', 'ACTIONS'].map(
                  (label) => (
                    <TableCell
                      key={label}
                      align="center"
                      sx={{
                        backgroundColor: '#f1f3f4',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                      }}
                    >
                      {label}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredSuppliers.map((supplier) => {
                const envColor = getEnvironmentColor(supplier.environment);
                const isOpen = openRowId === supplier.hotel_supplier_id;
                const editing = isEditing(supplier.hotel_supplier_id);
                const currentData = editing ? { ...supplier, ...editedData } : supplier;

                return (
                  <React.Fragment key={supplier.hotel_supplier_id}>
                    {/* Main Row */}
                    <TableRow hover sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                      <TableCell align="center">
                        {capitalize(supplier.hotel_supplier_name)}
                      </TableCell>

                      <TableCell align="center">
                        <Box
                          sx={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '16px',
                            backgroundColor: envColor.bgColor,
                            color: envColor.textColor,
                            border: `1px solid ${envColor.borderColor}`,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            textTransform: 'capitalize',
                          }}
                        >
                          {supplier.environment}
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        {capitalize(supplier.integrated_with)}
                      </TableCell>

                      <TableCell align="center">
                        <Box
                          sx={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '16px',
                            backgroundColor: supplier.isActive ? '#d1fae5' : '#fee2e2',
                            color: supplier.isActive ? '#059669' : '#dc2626',
                            border: `1px solid ${supplier.isActive ? '#a7f3d0' : '#fecaca'}`,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        >
                          {supplier.isActive ? 'True' : 'False'}
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {editing ? (
                            <>
                              <Tooltip title="Save">
                                <IconButton
                                  size="small"
                                  onClick={() => handleSave(supplier.hotel_supplier_id)}
                                  disabled={saveLoading}
                                  sx={{
                                    color: '#059669',
                                    '&:hover': { backgroundColor: '#d1fae5' },
                                  }}
                                >
                                  {saveLoading ? <CircularProgress size={20} /> : <Save />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton
                                  size="small"
                                  onClick={handleCancel}
                                  sx={{
                                    color: '#dc2626',
                                    '&:hover': { backgroundColor: '#fee2e2' },
                                  }}
                                >
                                  <Close />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => toggleViewRow(supplier.hotel_supplier_id)}
                              >
                                <Visibility />
                              </IconButton>
                              <IconButton
                                color="secondary"
                                size="small"
                                onClick={() => toggleEditRow(supplier.hotel_supplier_id, supplier)}
                              >
                                <Edit />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Collapsible Details Row */}
                    <TableRow>
                      <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 4, bgcolor: '#f5f5f5' }}>
                            {viewLoading && !editing ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                                <CircularProgress />
                              </Box>
                            ) : (
                              <Box className="feeModalForm" sx={{ bgcolor: '#fff', p: 3, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Hotel Supplier Configuration Details
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    {/* Status pill */}
                                    <Box
                                      onClick={() => editing && handleEditChange('isActive', !currentData.isActive)}
                                      sx={{
                                        height: 38,
                                        px: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.25,
                                        border: '1px solid',
                                        borderColor: currentData.isActive ? '#a7f3d0' : '#e5e7eb',
                                        borderRadius: '999px',
                                        bgcolor: currentData.isActive ? '#f0fdf4' : '#f9fafb',
                                        cursor: editing ? 'pointer' : 'default',
                                        transition: 'background-color 0.15s, border-color 0.15s',
                                        userSelect: 'none',
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: '50%',
                                          bgcolor: currentData.isActive ? '#059669' : '#9ca3af',
                                          boxShadow: currentData.isActive ? '0 0 0 3px rgba(5, 150, 105, 0.15)' : 'none',
                                        }}
                                      />
                                      <Typography
                                        sx={{
                                          fontSize: '13px',
                                          fontWeight: 600,
                                          color: currentData.isActive ? '#059669' : '#6b7280',
                                          letterSpacing: '0.2px',
                                        }}
                                      >
                                        {currentData.isActive ? 'Active' : 'Inactive'}
                                      </Typography>
                                      <Switch
                                        size="small"
                                        checked={!!currentData.isActive}
                                        color="success"
                                        disabled={!editing}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => handleEditChange('isActive', e.target.checked)}
                                        sx={{ ml: 0.25 }}
                                      />
                                    </Box>
                                    {editing && (
                                      <Button
                                        variant="contained"
                                        onClick={() => handleSave(supplier.hotel_supplier_id)}
                                        disabled={saveLoading}
                                        sx={{
                                          backgroundColor: '#272727',
                                          '&:hover': { backgroundColor: '#1a1a1a' },
                                          textTransform: 'none',
                                          fontWeight: 600,
                                        }}
                                      >
                                        {saveLoading ? <CircularProgress size={24} color="inherit" /> : 'Update'}
                                      </Button>
                                    )}
                                  </Box>
                                </Box>
                                <Box className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                  {/* Timeout */}
                                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                                    <label>Timeout (ms)</label>
                                    {editing ? (
                                      <input
                                        type="number"
                                        className="inputField"
                                        style={{ minWidth: '265px', height: '38px' }}
                                        value={currentData.timeout}
                                        onChange={(e) => handleEditChange('timeout', parseInt(e.target.value))}
                                      />
                                    ) : (
                                      <Typography sx={{ fontSize: '14px', color: '#333' }}>{currentData.timeout}ms</Typography>
                                    )}
                                  </Box>

                                  {/* Integrated With */}
                                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                                    <label>Integrated With</label>
                                    {editing ? (
                                      <input
                                        type="text"
                                        className="inputField"
                                        style={{ minWidth: '265px', height: '38px' }}
                                        value={currentData.integrated_with}
                                        onChange={(e) => handleEditChange('integrated_with', e.target.value)}
                                      />
                                    ) : (
                                      <Typography sx={{ fontSize: '14px', color: '#333' }}>{capitalize(currentData.integrated_with)}</Typography>
                                    )}
                                  </Box>

                                  {/* Supplier Prefix */}
                                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                                    <label>Supplier Prefix</label>
                                    {editing ? (
                                      <input
                                        type="text"
                                        className="inputField"
                                        style={{ minWidth: '265px', height: '38px' }}
                                        value={currentData.supplier_prefix}
                                        onChange={(e) => handleEditChange('supplier_prefix', e.target.value)}
                                      />
                                    ) : (
                                      <Typography sx={{ fontSize: '14px', color: '#333' }}>{currentData.supplier_prefix || '-'}</Typography>
                                    )}
                                  </Box>

                                  {/* Provider Mapping */}
                                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                                    <label>Provider Mapping</label>
                                    {editing ? (
                                      <input
                                        type="text"
                                        className="inputField"
                                        style={{ minWidth: '265px', height: '38px' }}
                                        value={currentData.provider_mapping}
                                        onChange={(e) => handleEditChange('provider_mapping', e.target.value)}
                                      />
                                    ) : (
                                      <Typography sx={{ fontSize: '14px', color: '#333' }}>{currentData.provider_mapping || '-'}</Typography>
                                    )}
                                  </Box>

                                  {/* Booking Enable */}
                                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                                    <label>Booking Enable</label>
                                    {editing ? (
                                      <Box
                                        onClick={() => handleEditChange('booking_enable', !currentData.booking_enable)}
                                        sx={{
                                          minWidth: 265,
                                          height: 38,
                                          px: 1.5,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          border: '1px solid',
                                          borderColor: currentData.booking_enable ? '#a7f3d0' : '#e5e7eb',
                                          borderRadius: '4px',
                                          bgcolor: currentData.booking_enable ? '#f0fdf4' : '#f9fafb',
                                          cursor: 'pointer',
                                          transition: 'background-color 0.15s, border-color 0.15s',
                                          userSelect: 'none',
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: currentData.booking_enable ? '#059669' : '#9ca3af' }} />
                                          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: currentData.booking_enable ? '#059669' : '#6b7280' }}>
                                            {currentData.booking_enable ? 'Enabled' : 'Disabled'}
                                          </Typography>
                                        </Box>
                                        <Switch
                                          size="small"
                                          checked={!!currentData.booking_enable}
                                          color="success"
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={(e) => handleEditChange('booking_enable', e.target.checked)}
                                        />
                                      </Box>
                                    ) : (
                                      <Typography sx={{ fontSize: '14px', color: '#333' }}>{currentData.booking_enable ? 'Enabled' : 'Disabled'}</Typography>
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}

              {loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}

              {!loading && filteredSuppliers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 8, fontWeight: 600, color: 'text.secondary' }}>
                    No suppliers found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Snackbar notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          top: '50% !important',
          left: '50% !important',
          transform: 'translate(-50%, -50%) !important',
          '& .MuiSnackbarContent-root': {
            textAlign: 'center',
            minWidth: '200px',
          },
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '1rem',
          }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default HotelSupplierConfiguration;
